import { useTranslation } from "react-i18next";
import { DueDiligencePlanOffer } from "../../Common/Hooks/useApi/Schemas";
import { DueDiligenceTier } from "../../Common/Types-DTOs/DueDiligence";
import {
  DUE_DILIGENCE_PLAN_DETAILS,
  DUE_DILIGENCE_TIER_LEVELS,
  formatMoney,
  levelsPlanCanPay,
  planFromLookupKey,
  planNameKey,
} from "../../Common/Utils/dueDiligencePlans";
import {
  PlanGrid,
  PlanOption,
  PlanName,
  PlanAudience,
  PlanPrice,
  PlanCredits,
  PlanCreditsNote,
  PlanExample,
  PlanExampleLabel,
  PlanOneOffPrice,
  PlanSavings,
  PlanNotes,
  PlanUnavailable,
} from "./Styles";

/** One-off price of each DD tier, in euros; null where Stripe has none. */
export type DueDiligenceOneOffPrices = Record<DueDiligenceTier, number | null>;

interface DueDiligencePlanPickerProps {
  plans: DueDiligencePlanOffer[];
  /** Null when none is selected, e.g. while a one-off payment is chosen instead. */
  selectedLookupKey: string | null;
  onSelect: (lookupKey: string) => void;
  /** To show what each plan's example would cost one-off; the example hides without them. */
  oneOffPrices?: DueDiligenceOneOffPrices | null;
  /** What each tier costs in credits, to tell which levels each plan reaches and what they cost. */
  creditCosts?: Record<DueDiligenceTier, number> | null;
  unavailablePlans?: Record<string, string>;
}

/**
 * The DD subscription plans as selectable cards, as the pricing one-pager
 * shows them. It only shows and selects: what happens next is up to whoever
 * uses it.
 */
export const DueDiligencePlanPicker = ({
  plans,
  selectedLookupKey,
  onSelect,
  oneOffPrices,
  creditCosts,
  unavailablePlans,
}: DueDiligencePlanPickerProps) => {
  const { t, i18n } = useTranslation();

  const money = (amount: number, currency: string) => formatMoney(amount, currency, i18n.language);

  /**
   * What the example month of a plan would cost paying each report one-off.
   * @returns Undefined when a needed one-off price is missing.
   */
  const oneOffTotal = (example: Partial<Record<DueDiligenceTier, number>>) => {
    let total = 0;
    for (const [tier, count] of Object.entries(example) as [DueDiligenceTier, number][]) {
      const price = oneOffPrices?.[tier];
      if (price == null) {
        return undefined;
      }
      total += price * count;
    }
    return total;
  };

  const describeExample = (example: Partial<Record<DueDiligenceTier, number>>) => {
    const entries = Object.entries(example) as [DueDiligenceTier, number][];
    const key = entries.length === 1 ? "dueDiligencePlans.example.single" : "dueDiligencePlans.example.item";
    return entries
      .map(([tier, count]) => t(key, { count, level: DUE_DILIGENCE_TIER_LEVELS[tier] }))
      .join(" + ");
  };

  /** Which levels a plan's credits reach, as one line under them. */
  const describeLevels = (plan: DueDiligencePlanOffer) => {
    const levels = levelsPlanCanPay(plan, creditCosts);
    if (levels.length === Object.keys(DUE_DILIGENCE_TIER_LEVELS).length) {
      return t("dueDiligencePlans.levels.all");
    }
    if (levels.length === 1) {
      return t("dueDiligencePlans.levels.one", { level: levels[0] });
    }
    return t("dueDiligencePlans.levels.some", { levels: levels.slice(0, -1).join(", "), last: levels[levels.length - 1] });
  };

  const plansWithoutChat = plans.filter((plan) => !plan.includesChat).map((plan) => t(planNameKey(plan.lookupKey)));

  return (
    <div>
      <PlanGrid role="radiogroup" aria-label={t("dueDiligencePlans.pickerLabel")}>
        {plans.map((plan) => {
          const isSelected = plan.lookupKey === selectedLookupKey;
          const planKey = planFromLookupKey(plan.lookupKey);
          const details = planKey ? DUE_DILIGENCE_PLAN_DETAILS[planKey] : undefined;
          const price = plan.price.unitAmount / 100;
          const currency = plan.price.currency;
          const exampleOneOff = details ? oneOffTotal(details.example) : undefined;
          const unavailableReason = unavailablePlans?.[plan.lookupKey];

          return (
            <PlanOption
              key={plan.lookupKey}
              type="button"
              role="radio"
              aria-checked={isSelected}
              $selected={isSelected}
              disabled={Boolean(unavailableReason)}
              onClick={() => onSelect(plan.lookupKey)}
            >
              <PlanName>{t(planNameKey(plan.lookupKey))}</PlanName>
              {planKey && <PlanAudience>{t(`dueDiligencePlans.audience.${planKey}`)}</PlanAudience>}
              <PlanPrice $selected={isSelected}>
                {money(price, currency)}
                <small>{t("dueDiligencePlans.perMonth")}</small>
              </PlanPrice>
              {unavailableReason ? (
                <PlanUnavailable>{unavailableReason}</PlanUnavailable>
              ) : (
                <>
                  <PlanCredits $selected={isSelected}>
                    {t("dueDiligencePlans.credits", { count: plan.monthlyCredits })}
                  </PlanCredits>
                  <PlanCreditsNote>{describeLevels(plan)}</PlanCreditsNote>
                </>
              )}
              {!unavailableReason && details && exampleOneOff !== undefined && (
                <PlanExample>
                  <PlanExampleLabel>{t("dueDiligencePlans.example.label")}</PlanExampleLabel>
                  <span>{describeExample(details.example)}</span>
                  <PlanOneOffPrice>
                    {t("dueDiligencePlans.example.oneOff", { price: money(exampleOneOff, currency) })}
                  </PlanOneOffPrice>
                  {exampleOneOff > price && (
                    <PlanSavings>
                      {t("dueDiligencePlans.example.savings", { amount: money(exampleOneOff - price, currency) })}
                    </PlanSavings>
                  )}
                </PlanExample>
              )}
            </PlanOption>
          );
        })}
      </PlanGrid>
      <PlanNotes>
        {plansWithoutChat.length > 0
          ? t("dueDiligencePlans.notes.chatExcept", { plans: plansWithoutChat.join(", ") })
          : t("dueDiligencePlans.notes.chatAll")}{" "}
        {creditCosts && <>{t("dueDiligencePlans.notes.creditCosts", creditCosts)} </>}
        {t("dueDiligencePlans.notes.vatExcluded")}
      </PlanNotes>
    </div>
  );
};
